import type { FC } from "react";
import { useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";

import { t } from "i18n/i18n";
import { CrossRemoveIcon, LoadingDotsIcon, PaperClipLinkIcon } from "mds/icons";

export type AttachmentChipProps = {
  className?: string;
  /** MIME type (e.g. from file.type or file.content_type). Used to show image preview when image/*. */
  contentType?: string;
  /** Optional File for pending uploads. When image, a preview is created from this (object URL). */
  file?: File;
  name: string;
  /** Called when remove button is clicked. When omitted, no remove button is shown. */
  onRemove?: () => void;
  /** When true, shows loading state instead of preview/icon. */
  uploading?: boolean;
  /** URL for image preview (server URL or object URL). Used when no file is provided. */
  url?: string;
};

const isImage = (contentType?: string) => contentType?.startsWith("image/") ?? false;

export const AttachmentChip: FC<AttachmentChipProps> = ({
  className,
  contentType,
  file,
  name,
  onRemove,
  uploading = false,
  url,
}) => {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);

  const contentTypeResolved = contentType ?? file?.type;
  const imageUrl = url ?? objectUrl;

  useEffect(() => {
    if (!file || !isImage(file.type)) return;
    const created = URL.createObjectURL(file);
    setObjectUrl(created);
    return () => {
      URL.revokeObjectURL(created);
      setObjectUrl(null);
    };
  }, [file]);

  const showImage = isImage(contentTypeResolved) && imageUrl && !uploading;

  return (
    <div
      className={twMerge(
        "border-black-tint-80 relative flex items-center gap-1.5 rounded-md border bg-black-tint-97 px-2 py-1 text-xs",
        className,
      )}
    >
      {uploading ? (
        <LoadingDotsIcon />
      ) : showImage ? (
        <img alt={name} className="size-6 rounded object-cover" src={imageUrl} />
      ) : (
        <PaperClipLinkIcon size="xs" />
      )}
      <span className="max-w-[120px] truncate">{name}</span>
      {onRemove && (
        <button
          aria-label={t("common.remove")}
          className="text-black-tint-40 hover:text-black"
          type="button"
          onClick={onRemove}
        >
          <CrossRemoveIcon size="xs" />
        </button>
      )}
    </div>
  );
};
